import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserOrders, updatePaymentDetails, getPaymentDetails } from '../services/api';
import Footer from './Footer';
import './Navbar.css'; // Reusing shared navbar styles
import './Dashboard.css'; // Reusing dashboard styles for navbar/drawer
import './Profile.css';

const Profile = ({ user, setUser }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState({
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        upiId: ''
    });
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [savingPayment, setSavingPayment] = useState(false);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const paymentMessageRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            if (user) {
                setLoading(true);
                try {
                    await Promise.all([fetchOrders(), fetchPaymentDetails()]);
                } catch (error) {
                    console.error("Error loading profile data:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Auto-scroll to payment message when it appears
    useEffect(() => {
        if (paymentMessage && paymentMessageRef.current) {
            paymentMessageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [paymentMessage]);

    const fetchOrders = async () => {
        try {
            const response = await getUserOrders(user.buyerId);
            if (response.success) {
                setOrders(response.data.orders || []);
            }
        } catch (err) {
            console.error('Failed to fetch orders');
        }
    };

    const fetchPaymentDetails = async () => {
        try {
            const response = await getPaymentDetails(user.buyerId);
            if (response.success && response.data.paymentDetails) {
                setPaymentDetails(response.data.paymentDetails);
            }
        } catch (err) {
            console.error('Failed to fetch payment details');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('cwUser');
        setUser(null);
        navigate('/');
    };

    const handlePaymentInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSavePaymentDetails = async () => {
        // Check if any bank field is filled
        const bankFieldsFilled = {
            bankName: !!paymentDetails.bankName,
            accountNumber: !!paymentDetails.accountNumber,
            ifscCode: !!paymentDetails.ifscCode,
            accountHolderName: !!paymentDetails.accountHolderName
        };

        const anyBankFieldFilled = Object.values(bankFieldsFilled).some(filled => filled);
        const allBankFieldsFilled = Object.values(bankFieldsFilled).every(filled => filled);
        const hasUpi = !!paymentDetails.upiId;

        // Validation: If any bank field is filled, all must be filled
        if (anyBankFieldFilled && !allBankFieldsFilled) {
            setPaymentMessage('Please provide all bank details (Bank Name, Account Number, IFSC Code, and Account Holder Name) or leave them empty');
            setTimeout(() => setPaymentMessage(''), 4000);
            return;
        }

        // Validation: At least one payment method must be provided
        if (!allBankFieldsFilled && !hasUpi) {
            setPaymentMessage('Please provide either complete bank details or UPI ID');
            setTimeout(() => setPaymentMessage(''), 3000);
            return;
        }

        setSavingPayment(true);
        setPaymentMessage('');

        try {
            const response = await updatePaymentDetails(user.buyerId, paymentDetails);
            if (response.success) {
                setPaymentMessage('Payment details saved successfully! You can no longer edit these details.');
                setIsEditingPayment(false);
                setTimeout(() => setPaymentMessage(''), 5000);
            } else {
                setPaymentMessage(response.message || 'Failed to save payment details');
                setTimeout(() => setPaymentMessage(''), 3000);
            }
        } catch (err) {
            setPaymentMessage('Failed to save payment details');
            setTimeout(() => setPaymentMessage(''), 3000);
        } finally {
            setSavingPayment(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingPayment(false);
        setPaymentMessage('');
    };

    // Check if payment details already exist
    const hasPaymentDetails = paymentDetails.bankName || paymentDetails.accountNumber ||
        paymentDetails.ifscCode || paymentDetails.accountHolderName ||
        paymentDetails.upiId;

    // Calculate statistics
    const activeOrders = orders.filter(order => 
        order.orderStatus !== 'Cancelled' && 
        order.affiliateStatus !== 'Cancelled'
    );

    const totalOrders = activeOrders.length;
    const paidOrders = activeOrders.filter(order => order.affiliateStatus === 'Paid').length;
    const pendingOrders = activeOrders.filter(order => order.affiliateStatus === 'Pending').length;
    const totalPaidAmount = activeOrders
        .filter(order => order.affiliateStatus === 'Paid')
        .reduce((sum, order) => {
            const amount = parseFloat(String(order.orderTotal).replace(/[^0-9.]/g, '')) || 0;
            return sum + amount;
        }, 0);

    return (
        <div className="profile-container">
            {/* Dashboard Navigation */}
            <nav className="home-nav">
                <div className="nav-content">
                    <div className="logo" onClick={() => navigate('/')}>
                        <img src="/cwLogo.png" alt="Collaboration World" className="navbar-logo-image" />
                    </div>
                    <div className="nav-links">
                        <div
                            className="profile-section"
                            onClick={() => { }}
                            style={{ cursor: 'default' }}
                            title="View Profile"
                        >
                            <div className="profile-icon-wrapper">
                                <i className="ri-user-line"></i>
                            </div>
                            <div className="profile-info">
                                <span className="profile-name-text">{user.fullName}</span>
                                <span className="profile-id">ID: {user.buyerId}</span>
                            </div>
                        </div>
                        <button onClick={handleLogout} className="logout-icon" title="Sign Out">
                            <i className="ri-logout-box-r-line"></i>
                        </button>

                        {/* Hamburger Button for Dashboard Drawer */}
                        <button className="dashboard-hamburger" onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
                            <i className="ri-menu-line"></i>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Side Drawer */}
            <div className={`dashboard-drawer ${isDrawerOpen ? 'active' : ''}`}>
                <button className="close-drawer-btn" onClick={() => setIsDrawerOpen(false)}>
                    <i className="ri-close-line"></i>
                </button>
                <div className="drawer-header">
                    <h3>Menu</h3>
                </div>
                <div className="drawer-content">
                    <button
                        className="drawer-link"
                        onClick={() => navigate('/dashboard', { state: { tab: 'deals' } })}
                    >
                        <i className="ri-gift-line"></i> Deals
                    </button>
                    <button
                        className="drawer-link"
                        onClick={() => navigate('/dashboard', { state: { tab: 'orders' } })}
                    >
                        <i className="ri-file-list-3-line"></i> My Orders
                    </button>
                    <button
                        className="drawer-link"
                        onClick={() => navigate('/dashboard', { state: { tab: 'submit' } })}
                    >
                        <i className="ri-add-circle-line"></i> Add Order
                    </button>
                    <button
                        className="drawer-link active"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        <i className="ri-user-line"></i> My Profile
                    </button>
                    <button
                        className="drawer-link"
                        onClick={() => navigate('/dashboard', { state: { tab: 'tnc' } })}
                    >
                        <i className="ri-file-shield-2-line"></i> Terms & Conditions
                    </button>
                    <button
                        className="drawer-link"
                        onClick={() => navigate('/dashboard', { state: { tab: 'sample-ss' } })}
                    >
                        <i className="ri-image-line"></i> Sample SS
                    </button>
                    <button
                        className="drawer-link"
                        onClick={handleLogout}
                        style={{ marginTop: 'auto', color: 'var(--cartier-burgundy)' }}
                    >
                        <i className="ri-logout-box-r-line"></i> Logout
                    </button>
                </div>
            </div>

            <div className="profile-content">
                <div className="profile-header">
                    <div className="profile-avatar">
                        <span className="avatar-icon"><i className="ri-user-line"></i></span>
                    </div>
                    <h1 className="profile-title">My Profile</h1>
                    <p className="profile-subtitle">Manage your account information</p>
                </div>

                <div className="profile-grid">
                    {/* Account Information Card */}
                    <div className="profile-card account-card">
                        <div className="card-header">
                            <h2>Account Information</h2>
                            <span className="card-icon"><i className="ri-file-list-3-line"></i></span>
                        </div>
                        <div className="card-body">
                            <div className="info-row">
                                <span className="info-label">Buyer ID</span>
                                <span className="info-value buyer-id">{user.buyerId}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Full Name</span>
                                <span className="info-value">{user.fullName}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Account Status</span>
                                <span className="status-badge active">Active</span>
                            </div>
                        </div>
                    </div>

                    {/* Order Statistics Card */}
                    <div className="profile-card stats-card">
                        <div className="card-header">
                            <h2>Order Statistics</h2>
                            <span className="card-icon"><i className="ri-bar-chart-grouped-line"></i></span>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="stats-grid">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="stat-item loading-stat-item" style={{ borderLeft: '4px solid var(--cartier-light-gray)' }}>
                                            <div className="skeleton skeleton-icon"></div>
                                            <div className="stat-info">
                                                <div className="skeleton skeleton-value"></div>
                                                <div className="skeleton skeleton-label"></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="stats-grid">
                                    <div className="stat-item">
                                        <div className="stat-icon"><i className="ri-box-3-line"></i></div>
                                        <div className="stat-info">
                                            <span className="stat-value">{totalOrders}</span>
                                            <span className="stat-label">Total Orders</span>
                                        </div>
                                    </div>
                                    <div className="stat-item success">
                                        <div className="stat-icon"><i className="ri-checkbox-circle-line"></i></div>
                                        <div className="stat-info">
                                            <span className="stat-value">{paidOrders}</span>
                                            <span className="stat-label">Paid Orders</span>
                                        </div>
                                    </div>
                                    <div className="stat-item success">
                                        <div className="stat-icon"><i className="ri-money-rupee-circle-line"></i></div>
                                        <div className="stat-info">
                                            <span className="stat-value">₹{totalPaidAmount.toLocaleString()}</span>
                                            <span className="stat-label">Total Paid Amount</span>
                                        </div>
                                    </div>
                                    <div className="stat-item warning">
                                        <div className="stat-icon"><i className="ri-time-line"></i></div>
                                        <div className="stat-info">
                                            <span className="stat-value">{pendingOrders}</span>
                                            <span className="stat-label">Pending</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Payment Details Card */}
                    <div className="profile-card payment-card">
                        <div className="card-header">
                            <h2>Payment Details</h2>
                            <span className="card-icon"><i className="ri-bank-card-line"></i></span>
                        </div>
                        <div className="card-body">
                            {paymentMessage && (
                                <div
                                    ref={paymentMessageRef}
                                    className={`payment-message ${paymentMessage.includes('success') ? 'success' : 'error'}`}
                                >
                                    {paymentMessage}
                                </div>
                            )}

                            <div className="payment-section">
                                <h3 className="section-title">Bank Details</h3>
                                <div className="payment-fields">
                                    <div className="field-group">
                                        <label>Bank Name</label>
                                        {loading ? (
                                            <div className="skeleton skeleton-input"></div>
                                        ) : isEditingPayment ? (
                                            <input
                                                type="text"
                                                name="bankName"
                                                value={paymentDetails.bankName}
                                                onChange={handlePaymentInputChange}
                                                placeholder="Enter bank name"
                                                className="payment-input"
                                            />
                                        ) : (
                                            <div className="field-value">
                                                {paymentDetails.bankName || 'Not provided'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="field-group">
                                        <label>Account Number</label>
                                        {loading ? (
                                            <div className="skeleton skeleton-input"></div>
                                        ) : isEditingPayment ? (
                                            <input
                                                type="text"
                                                name="accountNumber"
                                                value={paymentDetails.accountNumber}
                                                onChange={handlePaymentInputChange}
                                                placeholder="Enter account number"
                                                className="payment-input"
                                            />
                                        ) : (
                                            <div className="field-value">
                                                {paymentDetails.accountNumber || 'Not provided'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="field-group">
                                        <label>IFSC Code</label>
                                        {loading ? (
                                            <div className="skeleton skeleton-input"></div>
                                        ) : isEditingPayment ? (
                                            <input
                                                type="text"
                                                name="ifscCode"
                                                value={paymentDetails.ifscCode}
                                                onChange={handlePaymentInputChange}
                                                placeholder="Enter IFSC code"
                                                className="payment-input"
                                            />
                                        ) : (
                                            <div className="field-value">
                                                {paymentDetails.ifscCode || 'Not provided'}
                                            </div>
                                        )}
                                    </div>

                                    <div className="field-group">
                                        <label>Account Holder Name</label>
                                        {loading ? (
                                            <div className="skeleton skeleton-input"></div>
                                        ) : isEditingPayment ? (
                                            <input
                                                type="text"
                                                name="accountHolderName"
                                                value={paymentDetails.accountHolderName}
                                                onChange={handlePaymentInputChange}
                                                placeholder="Enter account holder name"
                                                className="payment-input"
                                            />
                                        ) : (
                                            <div className="field-value">
                                                {paymentDetails.accountHolderName || 'Not provided'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="payment-divider"></div>

                            <div className="payment-section">
                                <h3 className="section-title">UPI Details</h3>
                                <div className="payment-fields">
                                    <div className="field-group">
                                        <label>UPI ID</label>
                                        {loading ? (
                                            <div className="skeleton skeleton-input"></div>
                                        ) : isEditingPayment ? (
                                            <input
                                                type="text"
                                                name="upiId"
                                                value={paymentDetails.upiId}
                                                onChange={handlePaymentInputChange}
                                                placeholder="Enter UPI ID (e.g., user@paytm)"
                                                className="payment-input"
                                            />
                                        ) : (
                                            <div className="field-value">
                                                {paymentDetails.upiId || 'Not provided'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="payment-actions">
                                {isEditingPayment ? (
                                    <>
                                        <button
                                            onClick={handleSavePaymentDetails}
                                            className="btn-save"
                                            disabled={savingPayment}
                                        >
                                            {savingPayment ? 'Saving...' : 'Save Details'}
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="btn-cancel"
                                            disabled={savingPayment}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : hasPaymentDetails ? (
                                    <div className="admin-contact-message">
                                        <span className="info-icon"><i className="ri-information-line"></i></span>
                                        <p>To modify or update your payment details, please contact the administrator.</p>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setIsEditingPayment(true)}
                                        className="btn-edit"
                                    >
                                        Add Payment Details
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="profile-actions">
                    <button onClick={() => navigate('/dashboard')} className="btn-back">
                        <i className="ri-arrow-left-line"></i> Back to Dashboard
                    </button>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Profile;
